import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
  TablePagination,
  Button,
  Box,
  InputAdornment,
  Modal,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "8px",
};

const QueriesTable = ({ queries = [], isLoading, getQueries, tab }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [open, setOpen] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [btnLoading, setBtnLoading] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [answer, setAnswer] = useState("");

  const handleClose = () => {
    setOpen(false);
    setOpenRejectModal(false);
    setOpenViewModal(false);
    setAnswer("");
    setSelectedQuery(null);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = queries.map((n) => n.queryId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }
    setSelected(newSelected);
  };

  const handleViewQuery = (id) => {
    const query = queries.find((q) => q.queryId === id);
    setSelectedQuery(query);
    setAnswer("");
    setOpen(true);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const filteredQueries = useMemo(() => {
    return (queries || []).filter((q) =>
      q.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.vendorName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [queries, searchTerm]);

  const paginatedData = useMemo(() => {
    return filteredQueries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredQueries, page, rowsPerPage]);

  const handleRejectQuery = async () => {
    setBtnLoading(true);
    try {
      await Promise.all(
        selected.map((queryId) =>
          axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/query/reject-query?queryId=${queryId}`,
            null,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
          )
        )
      );
      setSelected([]);
      setOpenRejectModal(false);
      getQueries();
    } catch (error) {
      console.error(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleAnswerQuery = async () => {
    if (!answer.trim()) return alert("Please provide an answer.");
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/query`,
        {
          queryId: selectedQuery.queryId,
          answer: answer,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      handleClose();
      getQueries();
    } catch (error) {
      console.error(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const columnWidth = tab === 1 ? "22%" : "30%";

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-overlay"></div>}
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
        <Box display="flex" justifyContent={tab === 1 ? "space-between" : "flex-end"} alignItems="center" p={2}>
          {tab === 1 && (
            <Button
              variant="contained"
              color="error"
              disabled={selected.length === 0}
              onClick={() => setOpenRejectModal(true)}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Reject Selected ({selected.length})
            </Button>
          )}
          <TextField
            placeholder="Search Logistics Queries..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 350, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {tab === 1 && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < paginatedData.length}
                      checked={paginatedData.length > 0 && selected.length === paginatedData.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ width: columnWidth, fontWeight: "bold" }}>Subject/Title</TableCell>
                <TableCell sx={{ width: columnWidth, fontWeight: "bold" }}>Logistics Vendor</TableCell>
                <TableCell sx={{ width: columnWidth, fontWeight: "bold" }}>Date Received</TableCell>
                {(tab === 1 || tab === 2) && (
                  <TableCell sx={{ width: "15%", fontWeight: "bold" }} align="center">Action</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No queries found for this logistics draft.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow
                    key={row.queryId}
                    hover
                    selected={isSelected(row.queryId)}
                    sx={{ backgroundColor: index % 2 === 0 ? "#f9fcff" : "#ffffff" }}
                  >
                    {tab === 1 && (
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected(row.queryId)} onClick={() => handleClick(row.queryId)} />
                      </TableCell>
                    )}
                    <TableCell><strong>{row.title}</strong></TableCell>
                    <TableCell>{row.vendorName || "Unknown Vendor"}</TableCell>
                    <TableCell>{formatDateTime(row.createdDate)}</TableCell>
                    {(tab === 1 || tab === 2) && (
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedQuery(row);
                            tab === 1 ? setOpen(true) : setOpenViewModal(true);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={filteredQueries.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />

        {/* Answer Modal */}
        <Modal open={open} onClose={handleClose}>
          <Box sx={style}>
            <Typography variant="h6" mb={2} textAlign="center">Logistics Query Response</Typography>
            <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
            <Typography mb={2}>{selectedQuery?.title}</Typography>
            <Typography variant="subtitle2" color="textSecondary">Message:</Typography>
            <Typography mb={2} sx={{ whiteSpace: 'pre-wrap' }}>{selectedQuery?.body}</Typography>

            <Typography variant="subtitle2" mb={1}>Your Answer:</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Type your response to the vendor here..."
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button onClick={handleClose}>Cancel</Button>
              <Button variant="contained" onClick={handleAnswerQuery} disabled={btnLoading}>
                Submit Answer
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* View Modal (For Answered Queries) */}
        <Modal open={openViewModal} onClose={handleClose}>
          <Box sx={style}>
            <Typography variant="h6" mb={2} textAlign="center">Query Details</Typography>
            <Box mb={2}>
              <Typography variant="subtitle2" color="primary">Vendor Message:</Typography>
              <Typography p={1} sx={{ bgcolor: '#f5f5f5', borderRadius: '4px' }}>{selectedQuery?.body}</Typography>
            </Box>
            <Box mb={3}>
              <Typography variant="subtitle2" color="success.main">Your Response:</Typography>
              <Typography p={1} sx={{ bgcolor: '#fafffa', border: '1px solid #e0eee0', borderRadius: '4px' }}>
                {selectedQuery?.answer || "No answer provided."}
              </Typography>
            </Box>
            <Button fullWidth variant="outlined" onClick={handleClose}>Close</Button>
          </Box>
        </Modal>

        {/* Reject Confirmation */}
        <Modal open={openRejectModal} onClose={handleClose}>
          <Box sx={style}>
            <Typography variant="h6" color="error" mb={2}>Reject Selected Queries?</Typography>
            <Typography mb={3}>Are you sure you want to reject {selected.length} query(ies)? This action cannot be undone.</Typography>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button onClick={handleClose}>Cancel</Button>
              <Button variant="contained" color="error" onClick={handleRejectQuery}>Confirm Reject</Button>
            </Box>
          </Box>
        </Modal>
      </Paper>
    </div>
  );
};

export default QueriesTable;
